import React from 'react';
import { Star } from 'react-feather';
import PropTypes from 'prop-types';

/**
 * StarRating Component
 * Displays a star rating with optional review count
 *
 * @param {number} rating - Rating value (e.g., 4.4)
 * @param {number} maxRating - Maximum rating (default: 5)
 * @param {number} reviews - Number of reviews
 * @param {string} platform - Platform name (e.g., "Capterra")
 * @param {string} size - Icon size: 'sm', 'md', 'lg'
 */
const StarRating = ({ rating, maxRating = 5, reviews, platform, size = 'md' }) => {
    const sizeMap = {
        sm: 14,
        md: 18,
        lg: 24,
    };

    const iconSize = sizeMap[size] || sizeMap.md;

    // Calculate filled, half, and empty stars
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.3 && rating % 1 < 0.8;
    const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

    const renderStars = () => {
        const stars = [];

        // Full stars
        for (let i = 0; i < fullStars; i++) {
            stars.push(
                <Star
                    key={`full-${i}`}
                    size={iconSize}
                    fill="#F59E0B"
                    stroke="#F59E0B"
                    style={{ marginRight: '2px' }}
                />
            );
        }

        // Half star
        if (hasHalfStar) {
            stars.push(
                <div
                    key="half"
                    style={{ position: 'relative', display: 'inline-block', marginRight: '2px' }}
                >
                    <Star size={iconSize} fill="none" stroke="#F59E0B" />
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '50%',
                            overflow: 'hidden',
                        }}
                    >
                        <Star size={iconSize} fill="#F59E0B" stroke="#F59E0B" />
                    </div>
                </div>
            );
        }

        // Empty stars
        for (let i = 0; i < emptyStars; i++) {
            stars.push(
                <Star
                    key={`empty-${i}`}
                    size={iconSize}
                    fill="none"
                    stroke="#D1D5DB"
                    style={{ marginRight: '2px' }}
                />
            );
        }

        return stars;
    };

    return (
        <div
            className="star-rating"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>{renderStars()}</div>
            {(reviews || platform) && (
                <span style={{ fontSize: size === 'sm' ? '13px' : '14px', color: '#FFFFFF' }}>
                    {rating.toFixed(1)}
                    {reviews && ` (${reviews.toLocaleString()} reviews)`}
                    {platform && ` on ${platform}`}
                </span>
            )}
        </div>
    );
};

StarRating.propTypes = {
    rating: PropTypes.number.isRequired,
    maxRating: PropTypes.number,
    reviews: PropTypes.number,
    platform: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default StarRating;
